import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const RANGES = [7, 30, 90];

export default function MarketplaceAnalytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/admin/marketplace/analytics", { params: { days } }).then((r) => setD(r.data)).catch(() => toast.error("Could not load analytics"));
  }, [days]);

  return (
    <div data-testid="mkt-analytics">
      <div className="flex gap-2 mb-6">
        {RANGES.map((r) => (
          <button key={r} data-testid={`mkt-range-${r}`} onClick={() => setDays(r)}
            className={`px-3 py-1.5 text-[10px] tracking-widest uppercase rounded border ${days === r ? "bg-white text-black border-white" : "border-white/15 text-neutral-400"}`}>{r} days</button>
        ))}
      </div>
      {!d ? <Loader2 className="animate-spin text-[#D4AF37]" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi label="Homepage visits" value={d.visits} testid="mkt-kpi-visits" />
            <Kpi label="Unique sessions" value={d.unique_sessions} testid="mkt-kpi-sessions" />
            <Kpi label="Product views" value={d.views} testid="mkt-kpi-views" />
            <Kpi label="Outbound clicks" value={d.clicks} testid="mkt-kpi-clicks" />
            <Kpi label="Click-through" value={`${d.ctr}%`} testid="mkt-kpi-ctr" />
          </div>
          <div className="luxury-card rounded-xl p-5 mt-6 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.daily} margin={{ left: -20, right: 8 }}>
                <CartesianGrid stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#737373", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fill: "#737373", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="visit" name="Visits" stroke="#D4AF37" fill="#D4AF3722" dot={{ r: 2 }} />
                <Area type="monotone" dataKey="view" name="Views" stroke="#7dd3fc" fill="#7dd3fc11" dot={{ r: 2 }} />
                <Area type="monotone" dataKey="click" name="Clicks" stroke="#34d399" fill="#34d39922" dot={{ r: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <table className="w-full text-sm mt-6" data-testid="mkt-top-listings">
            <thead><tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400">
              <th className="text-left py-3">Top listings</th><th className="text-right">Views</th><th className="text-right">Clicks</th><th className="text-right">CTR</th>
            </tr></thead>
            <tbody>
              {d.top_listings.map((t) => (
                <tr key={t.listing_id} className="border-b border-white/5">
                  <td className="py-2.5 text-white">{t.title}</td><td className="text-right text-neutral-300">{t.views}</td>
                  <td className="text-right text-[#D4AF37]">{t.clicks}</td><td className="text-right text-neutral-400">{t.ctr}%</td>
                </tr>
              ))}
              {!d.top_listings.length && <tr><td colSpan={4} className="py-6 text-center text-neutral-500">No views or clicks yet in this period.</td></tr>}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="luxury-card rounded-xl p-5">
      <div className="text-[10px] tracking-widest uppercase text-neutral-500">{label}</div>
      <div data-testid={testid} className="font-serif-display text-3xl text-white mt-1">{value}</div>
    </div>
  );
}
